import React, {useEffect, useRef, useState} from 'react';
import { toWkt } from './wkt-helper';
import { t } from '../common/LocalizationProvider'

import TimelineIcon from '@material-ui/icons/Timeline';
import CropFreeIcon from '@material-ui/icons/CropFree';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import Tooltip from "@material-ui/core/Tooltip";
import Divider from "@material-ui/core/Divider";

const GeofenceEditGoogleMap = ({ map, googleMapsApi, handleClickOpen, setNewItem }) => {

    const [drawingMode, setDrawingMode] = useState(null); // 'POLYGON', 'LINESTRING', 'CIRCLE', or null
    const drawingShapeRef = useRef(null);
    const mapClickListenerRef = useRef(null);
    const mouseMoveListenerRef = useRef(null);

    // --- 2. Effect to manage the drawing process (now includes CIRCLE) ---
    useEffect(() => {
        if (!map || !googleMapsApi) return;

        const handleDrawingComplete = () => {
            if (!drawingShapeRef.current) return;

            // Make shape non-interactive
            drawingShapeRef.current.setEditable(false);
            drawingShapeRef.current.setDraggable(false);

            const wktString = toWkt(drawingShapeRef.current, drawingMode);
            setNewItem({ area: wktString });
            handleClickOpen();

            // Clean up drawing state
            if (mouseMoveListenerRef.current) {
                googleMapsApi.event.removeListener(mouseMoveListenerRef.current);
                mouseMoveListenerRef.current = null;
            }
            drawingShapeRef.current = null;
            setDrawingMode(null);
        };

        const handleMapClick = (event) => {
            if (drawingMode === 'POLYGON' || drawingMode === 'LINESTRING') {
                const path = drawingShapeRef.current ? drawingShapeRef.current.getPath() : null;
                if (path) {
                    path.push(event.latLng);
                } else {
                    const shapeOptions = {map, editable: true, draggable: true, strokeColor: '#008000', strokeOpacity: 0.8, strokeWeight: 3,};
                    if (drawingMode === 'POLYGON') {
                        drawingShapeRef.current = new googleMapsApi.Polygon({ ...shapeOptions, fillColor: '#008000', fillOpacity: 0.2, paths: [event.latLng] });
                    } else {
                        drawingShapeRef.current = new googleMapsApi.Polyline({ ...shapeOptions, path: [event.latLng] });
                    }
                    if (drawingShapeRef.current) {
                        googleMapsApi.event.addListener(drawingShapeRef.current, 'dblclick', handleDrawingComplete);
                    }
                }
            }
            else if (drawingMode === 'CIRCLE') {
                if (!drawingShapeRef.current) {
                    // First click: Create the circle and set up mouse move listener
                    drawingShapeRef.current = new googleMapsApi.Circle({
                        map, center: event.latLng, radius: 1, editable: true, draggable: true, strokeColor: '#008000', fillColor: '#008000', fillOpacity: 0.2,
                    });
                    mouseMoveListenerRef.current = googleMapsApi.event.addListener(map, 'mousemove', (e) => {
                        const radius = googleMapsApi?.geometry?.spherical?.computeDistanceBetween(event.latLng, e.latLng);
                        drawingShapeRef.current.setRadius(radius);
                    });
                    if (drawingShapeRef.current) {
                        googleMapsApi.event.addListener(drawingShapeRef.current, 'dblclick', handleDrawingComplete);
                    }
                }
                else {
                    // Second click: Finalize the drawing
                    handleDrawingComplete();
                }
            }
        };

        if (drawingMode) {
            map.setOptions({ draggableCursor: 'crosshair' });
            mapClickListenerRef.current = googleMapsApi.event.addListener(map, 'click', handleMapClick);
        }

        return () => {
            map.setOptions({ draggableCursor: 'grab' });
            if (mapClickListenerRef.current) {
                googleMapsApi.event.removeListener(mapClickListenerRef.current);
                mapClickListenerRef.current = null;
            }
            if (mouseMoveListenerRef.current) {
                googleMapsApi.event.removeListener(mouseMoveListenerRef.current);
                mouseMoveListenerRef.current = null;
            }
            if (drawingShapeRef.current) {
                googleMapsApi.event.clearInstanceListeners(drawingShapeRef.current);
                drawingShapeRef.current.setMap(null);
                drawingShapeRef.current = null;
            }
        };
    }, [map, googleMapsApi, drawingMode, setNewItem, handleClickOpen]);

    const handleCancelDrawing = () => {
        setDrawingMode(null); // Triggers the cleanup in the useEffect
    };

    const getInstruction = () => {
        switch (drawingMode) {
            case 'CIRCLE':
                return t('circleInstruction');
            case 'POLYGON':
            case 'LINESTRING':
                return t('polylineLineStringInstruction');
            default:
                return '';
        }
    };

    // --- 3. Render UI controls for drawing ---
    return (
        <div style={{ position: 'absolute', bottom: 30, left: 10, zIndex: 1, background: 'white', padding: '5px', borderRadius: '4px', boxShadow: '0 2px 6px rgba(0,0,0,.3)' }}>
            {drawingMode ? (
                <>
                    <p style={{ margin: '0 0 5px 0', fontSize: '12px', textAlign: 'center', minWidth: '200px' }}>
                        {getInstruction()}
                    </p>
                    <button onClick={handleCancelDrawing} style={{ width: '100%', cursor: 'pointer' }}>
                        {t('sharedCancel')}
                    </button>
                </>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',  // Stacks buttons vertically
                    gap: '10px',              // Creates space between the buttons
                    marginBottom: '0px'         // Adds space above the entire container
                }}>
                    <Tooltip title={t('Polygon')} placement="right">
                        <CropFreeIcon onClick={() => setDrawingMode('POLYGON')} style={{ cursor: 'pointer' }}>
                        </CropFreeIcon>
                    </Tooltip>
                    <Divider />
                    <Tooltip title={t('Line')} placement="right">
                        <TimelineIcon onClick={() => setDrawingMode('LINESTRING')} style={{ cursor: 'pointer' }}>
                        </TimelineIcon>
                    </Tooltip>
                    <Divider />
                    <Tooltip title={t('Circle')} placement="right">
                        <RadioButtonUncheckedIcon onClick={() => setDrawingMode('CIRCLE')} style={{ cursor: 'pointer' }}>
                        </RadioButtonUncheckedIcon>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

export default GeofenceEditGoogleMap;